import React, { useState, forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { formatNumber } from 'shared/functions';
import { grey } from '@mui/material/colors';
import { COLORS } from '@scope/scope-one-ui/theme';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  tabs: {
    padding: theme.spacing(2, 1),
    minHeight: 32,
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  tab: {
    minWidth: 130,
    minHeight: 32,
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: grey[800],
    borderRadius: 16,
    fontSize: 14,
    textTransform: 'none',
    padding: '3px 6px',
    margin: 5,
    '&.MuiTab-textColorInherit': {
      opacity: 0.4,
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderWidth: 2,
      borderStyle: 'solid',
      borderColor: theme.palette.primary.main,
      opacity: 1,
    },
  },
  shadowed: {
    borderRadius: theme.spacing(1),
    boxShadow: `4px 4px 2px lightgrey`,
    marginBottom: theme.spacing(0.5),
    backgroundColor: COLORS.secondary.main,
  },
}));

const useTabStyles = makeStyles({
  root: {
    backgroundColor: COLORS.secondary.main,
    maxWidth: 300,
    minWidth: 160,
  },
});

const TabsPanel = forwardRef(({ children, active, variant, onChange, hasShadow, lazy }, ref) => {
  const childrenArray = Array.isArray(children)
    ? children.filter((child) => !!child).flat(1)
    : [children];
  const [tabsLoadedState, setTabsLoadedState] = useState(
    childrenArray.reduce((o, child) => ({ ...o, [child.props.value]: !lazy }), {}),
  );
  const [clonedChildren, setClonedChildren] = useState([]);

  const [activeTab, setActiveTab] = useState(active);
  const classes = useStyles();
  const tabClasses = useTabStyles();

  const handleChange = (_, value) =>
    onChange ? onChange('companyTab', value) : setActiveTab(value);

  const selectActive = onChange ? active : activeTab;

  useEffect(() => {
    setActiveTab(active);
  }, [active]);

  useEffect(() => {
    setTabsLoadedState({ ...tabsLoadedState, [selectActive]: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectActive]);

  useEffect(() => {
    setClonedChildren(
      React.Children.map(childrenArray, (child) =>
        tabsLoadedState[child.props.value]
          ? React.cloneElement(child, { hidden: child.props.value !== selectActive })
          : undefined,
      ).filter((child) => child),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsLoadedState, children]);

  return (
    <Paper ref={ref} className={classes.root}>
      <Tabs
        value={selectActive}
        onChange={handleChange}
        TabIndicatorProps={{
          style: {
            minWidth: 160,
          },
        }}
        variant={variant === 'scrollable' ? 'scrollable' : 'standard'}
        scrollButtons={variant === 'scrollable' ? 'auto' : false}
        className={clsx(
          {
            [classes.tabs]: variant === 'chip',
          },
          hasShadow ? classes.shadowed : '',
        )}
      >
        {childrenArray.map((child) => {
          const { label, value, counter, total } = child.props;

          let counterInfo = '';
          if (!isNil(counter)) {
            counterInfo = isNil(total)
              ? ` (${formatNumber(counter)})`
              : ` (${formatNumber(total)} requests available)`;
          }

          return (
            <Tab
              className={clsx({
                [classes.tab]: variant === 'chip',
              })}
              classes={tabClasses}
              label={`${label}${counterInfo}`}
              key={value}
              value={value}
            />
          );
        })}
      </Tabs>
      {clonedChildren}
    </Paper>
  );
});

TabsPanel.defaultProps = {
  variant: 'default',
  onChange: undefined,
  hasShadow: '',
  lazy: true,
};

TabsPanel.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node.isRequired),
    PropTypes.node.isRequired,
  ]).isRequired,
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  variant: PropTypes.oneOf(['default', 'chip', 'scrollable']),
  hasShadow: PropTypes.string,
  lazy: PropTypes.bool,
};

export default TabsPanel;
